import React from "react";
import PropTypes from "prop-types";
import AddCircle from "@material-ui/icons/AddCircle";
import BWModels from "@benjamin-west-rpm2/benjamin-west-models";
import _isEmpty from "lodash/isEmpty";
import memoizeOne from "memoize-one";
import { Box } from "@material-ui/core";

import { Content } from "../../layout/Content";
import Paper from "../../mui/core/Paper";
import { BWGridAPI, GridHeader, GridTable } from "../../ui/BWGrid";
import Vendor from "../../../models/Vendor";
import propTypes from "../../../constants/propTypes";
import AddressBook from "../AddressBook/AddressBook";
import { genDetailRowComponents } from "../../ui/BWGrid/helperComponents/SpecAdditionalInfoDetailRow";
import { RowComponent } from "../../ui/BWGrid/helperComponents/EditableTextDetailRow/genDetailRowComponents/v2";
import PagingPaneWithStatusSwitch from "../../ui/BWGrid/PagingPaneWithStatusSwitch";

import VendorStatusTag from "./VendorStatusTag";
import { parseVendorName } from "../VendorCreate/VendorCreateContainer";
import ProductsRenderer from "./ProductsRenderer";
import CertificationsRenderer from "./CertificationsRenderer";
import { useFetchFilterOptions } from "./useFetchFilterOptions";

export const getRowMenu = ({
  handleEditVendor,
  handleDeleteVendorClick,
  handleArchiveVendor,
  handleUnarchiveVendor,
  canArchiveVendors,
  canUnarchiveVendors,
}) => row => {
  const menuItems = [{ text: "Edit", onClick: () => handleEditVendor(row.id) }];

  if (row.isArchived && canUnarchiveVendors) {
    menuItems.push({
      text: "Unarchive",
      onClick: () => handleUnarchiveVendor(row),
    });
  } else if (!row.isArchived && canArchiveVendors) {
    menuItems.push({
      text: "Archive",
      onClick: () => handleArchiveVendor(row),
    });
  }

  menuItems.push({ separator: true });

  menuItems.push({
    text: "Delete",
    onClick: () => handleDeleteVendorClick(row),
  });

  return menuItems;
};

const getStatusOptions = () => BWModels.loadSchema("Vendor").__utils.statuses;

const relationFilter = ({ value }) => ({
  $or: [
    {
      name: {
        $ilike: `%${parseVendorName(value)}%`,
      },
    },
    {
      poName: {
        $ilike: `%${parseVendorName(value)}%`,
      },
    },
    {
      w9Name: {
        $ilike: `%${parseVendorName(value)}%`,
      },
    },
    {
      brands: {
        $ilikeContains: `%${parseVendorName(value)}%`,
      },
    },
  ],
});

const getTableComponents = memoizeOne((dataComponentId, displayComments) => {
  if (!displayComments) return {};
  return {
    ...genDetailRowComponents(
      "id",
      [
        {
          path: "comments",
          label: "",
          placeholder: "Add Comment",
          isEmptyFn: Boolean,
          showLabel: false,
        },
      ],
      BWModels.loadSchema("Vendor"),
      dataComponentId,
      { prevSpan: 2, colSpan: 4, posSpan: 2 }
    ),
    RowComponent,
  };
});

const VendorsPage = ({
  addressBook,
  dataComponent,
  handleRowClick,
  handleEditVendor,
  handleAddVendorClick,
  handleDeleteVendorClick,
  handleArchiveVendor,
  handleUnarchiveVendor,
  canArchiveVendors,
  canUnarchiveVendors,
  vendorCategories,
  showArchived,
}) => {
  const { products, certifications } = useFetchFilterOptions();

  return (
    <Content>
      <Paper>
        <BWGridAPI
          apiRoute="vendors"
          model={Vendor}
          includes={["category", "certifications", "products"]}
          dataComponent={dataComponent}
          defaultSorting={[
            {
              columnName: "name",
              direction: "asc",
            },
          ]}
          apiFilters={{
            params: { omitScopeFilter: true },
            rootFilters: showArchived
              ? {
                  displayArchived: true,
                }
              : {},
          }}
          tableComponents={{
            ...getTableComponents(dataComponent.dataComponentId, true),
            PagingPanelTemplate: PagingPaneWithStatusSwitch,
          }}
          alwaysDisplayDetailRow
        >
          <GridHeader
            headerText={`${dataComponent.totalRows} Vendors`}
            actions={[
              {
                text: "ADD VENDOR",
                icon: <AddCircle />,
                handler: handleAddVendorClick,
              },
            ]}
          />
          <GridTable
            columns={[
              "name",
              "status",
              "certifications.id",
              "products.id",
              "category.name",
              "cleanWebsite",
            ]}
            columnOptions={{
              name: {
                bold: true,
                filter: true,
                filterOptions: {
                  operator: relationFilter,
                },
                render: vendor => {
                  const label = Vendor.labelProperty(vendor);
                  return _isEmpty(vendor.brands)
                    ? label
                    : `${label} - ${vendor.brands.join(", ")}`;
                },
              },
              status: {
                filter: "multiselect",
                wordWrapEnabled: true,
                filterOptions: {
                  operator: "jsonIn",
                  options: getStatusOptions(),
                },
                render: ({ status }) =>
                  status && (
                    <Box display={"flex"} flexWrap={"wrap"} gridGap={"4px"}>
                      {status.map(status => (
                        <VendorStatusTag key={status}>{status}</VendorStatusTag>
                      ))}
                    </Box>
                  ),
              },
              "certifications.id": {
                title: "Certifications",
                filter: "multiselect",
                wordWrapEnabled: true,
                minWidth: "150px",
                render: CertificationsRenderer,
                filterOptions: {
                  options: certifications,
                },
              },
              "products.id": {
                title: "Products",
                filter: "multiselect",
                wordWrapEnabled: true,
                minWidth: "150px",
                render: ProductsRenderer,
                filterOptions: {
                  options: products,
                },
              },
              "category.name": {
                title: "Category",
                align: "right",
                filter: "select",
                filterOptions: {
                  options: vendorCategories,
                },
              },
              cleanWebsite: { align: "right", title: "Website" },
            }}
            rowMenu={getRowMenu({
              handleEditVendor,
              handleDeleteVendorClick,
              handleArchiveVendor,
              handleUnarchiveVendor,
              canArchiveVendors,
              canUnarchiveVendors,
            })}
            onClick={handleRowClick}
          />
        </BWGridAPI>
      </Paper>
      <Paper>
        <AddressBook addressBook={addressBook} />
      </Paper>
    </Content>
  );
};

VendorsPage.propTypes = {
  handleRowClick: PropTypes.func.isRequired,
  dataComponent: PropTypes.object.isRequired,
  handleEditVendor: PropTypes.func.isRequired,
  handleAddVendorClick: PropTypes.func.isRequired,
  handleDeleteVendorClick: PropTypes.func.isRequired,
  handleArchiveVendor: PropTypes.func.isRequired,
  handleUnarchiveVendor: PropTypes.func.isRequired,
  canArchiveVendors: PropTypes.bool,
  canUnarchiveVendors: PropTypes.bool,
  vendorCategories: PropTypes.arrayOf(propTypes.vendorCategory),
  addressBook: PropTypes.object.isRequired,
  showArchived: PropTypes.bool,
};
VendorsPage.defaultProps = {
  handleRowClick: () => {},
  dataComponent: {},
  handleEditVendor: () => {},
  handleAddVendorClick: () => {},
  handleDeleteVendorClick: () => {},
  handleArchiveVendor: () => {},
  handleUnarchiveVendor: () => {},
  canArchiveVendors: false,
  canUnarchiveVendors: false,
  vendorCategories: [],
  addressBook: {},
  showArchived: false,
};

export default VendorsPage;
